import { trackStructEvent } from "@snowplow/browser-tracker";
import { useCallback, useContext, useRef, useState } from "react";

import { sendChatbotMessage } from "@/apis/ChatbotApi";
import { EmbeddedMetric, Logger } from "@/apis/Logger";
import { AIAgentSessionHelper } from "@/helpers/agentcore/ai-agent-session-helper";
import { getOS } from "@/helpers/os-helper";
import { AppContext } from "@/stores/appStore";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import type { ChatbotContext, ChatMessage } from "../@types/agentcore";

/**
 * Hook that manages chatbot conversation state and message sending.
 */
export const useChatbot = () => {
  const { state } = useContext(AppContext);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [escalationSummary, setEscalationSummary] = useState("");
  const [escalationInitiated, setEscalationInitiated] = useState(false);
  const sessionRef = useRef<{ sessionId: string; actorId: string } | null>(
    null
  );
  const stateRef = useRef(state);
  stateRef.current = state;

  const buildContext = (): ChatbotContext => {
    const s = stateRef.current;
    const allDone = s.steps.every((step) => step.completed);
    return {
      current_step: allDone ? "D1_COMPLETE" : s.currentStep?.id ?? "",
      current_sub_step: "",
      platform: getOS(),
      hiring_country: s.hiringCountry ?? "",
      verification_status: s.docsVerificationStatus ?? "",
      active_errors: [],
      alias: s.amazonUsername ?? "",
      language: s.selectedLanguage,
    };
  };

  // CloudWatch dimensions and log context attached to chatbot telemetry.
  const chatbotLogContext = (): Record<string, unknown> => ({
    chatbotSessionId: sessionRef.current?.sessionId,
    step: stateRef.current.currentStep?.id,
    region: stateRef.current.activeRegion,
    language: stateRef.current.selectedLanguage,
  });

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      // Lazy-init session
      if (!sessionRef.current) {
        sessionRef.current = AIAgentSessionHelper.createSession();
        trackStructEvent({
          category: Day1EventCategory.Chatbot,
          action: Day1EventAction.ChatbotSessionStarted,
          property: sessionRef.current.sessionId,
        });
      }

      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: text,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      trackStructEvent({
        category: Day1EventCategory.Chatbot,
        action: Day1EventAction.ChatbotMessageSent,
        value: text.trim().length,
      });

      const requestStart = Date.now();
      try {
        const response = await sendChatbotMessage({
          agent_type: "chatbot",
          message: text,
          session_id: sessionRef.current.sessionId,
          actor_id: sessionRef.current.actorId,
          context: buildContext(),
        });

        const latencyMs = Date.now() - requestStart;
        trackStructEvent({
          category: Day1EventCategory.Chatbot,
          action: Day1EventAction.ChatbotResponseReceived,
          value: latencyMs,
        });
        Logger.publishInfoMetric(
          new EmbeddedMetric(
            "ChatbotResponseLatency",
            "Milliseconds",
            latencyMs
          ),
          "Chatbot response received",
          undefined,
          chatbotLogContext()
        );

        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: response.message,
          timestamp: Date.now(),
          suggestedTopics: response.suggested_topics,
          media: response.media?.length ? response.media : undefined,
        };

        if (response.escalation_summary) {
          setEscalationSummary(response.escalation_summary);
        }

        if (response.escalation_initiated) {
          setEscalationInitiated(true);
          trackStructEvent({
            category: Day1EventCategory.Chatbot,
            action: Day1EventAction.ChatbotEscalationInitiated,
          });
        }

        setMessages((prev) => [...prev, assistantMsg]);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        trackStructEvent({
          category: Day1EventCategory.Chatbot,
          action: Day1EventAction.ChatbotResponseError,
        });
        Logger.publishErrorMetric(
          new EmbeddedMetric("ChatbotResponseError", "Count", 1),
          `Chatbot error: ${errorMessage}`,
          undefined,
          chatbotLogContext()
        );
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content:
              "I'm having trouble responding right now. Please try again or contact IT Support.",
            timestamp: Date.now(),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    sessionRef.current = null;
    setEscalationSummary("");
    setEscalationInitiated(false);
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    clearChat,
    escalationSummary,
    escalationInitiated,
  };
};
